import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

import { Card } from "@/components/ui/card";

/**
 * What a successful Flow submission leaves on the page (fs#1797).
 *
 * The toast used to be the ONLY outcome: it faded after a few seconds, and
 * nothing on the page led from the submission to the runs it had just created
 * — a Tile submission navigates to its run, a Flow submission left the user
 * where they were with no way forward. This panel stays until the next
 * submission replaces it, names the flow run the server minted, and links
 * back to the project's runs, where the flow's children land as they start.
 *
 * Props-only, no store or query coupling, so it can move to `flowstack-ui`
 * unchanged.
 */
export interface FlowSubmittedNoticeProps {
  /** Project slug — the runs link goes back to this project's page. */
  projectSlug: string;
  flowName: string;
  /** The durable flow-run id `POST /api/flows/submit` returns. Absent from
   * an older API; the notice then omits the sentence rather than print
   * "undefined". */
  flowRunId?: string;
  /** Compiled node count; fan-out children are counted as they dispatch. */
  nodeCount: number;
}

export function FlowSubmittedNotice({
  projectSlug,
  flowName,
  flowRunId,
  nodeCount,
}: FlowSubmittedNoticeProps): JSX.Element {
  return (
    <Card
      as="section"
      role="status"
      aria-labelledby="flow-submitted-heading"
      className="flex flex-col gap-2 p-card"
    >
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-state-completed-fg" aria-hidden />
        <h2
          id="flow-submitted-heading"
          className="font-display text-base font-semibold tracking-tight"
        >
          Flow submitted
        </h2>
      </div>
      <p className="text-sm text-muted-foreground">
        <span className="font-mono text-foreground">{flowName}</span> is dispatching {nodeCount}{" "}
        {nodeCount === 1 ? "node" : "nodes"}. Each node, and each fan-out child, runs as its own run
        and appears in the run feed as it starts.
        {flowRunId ? (
          <>
            {" "}
            Flow run <span className="font-mono text-foreground">{flowRunId}</span>.
          </>
        ) : null}
      </p>
      <div className="flex flex-wrap items-center gap-4 text-sm font-medium">
        <Link
          to={`/projects/${projectSlug}`}
          className="text-accent underline-offset-4 hover:underline"
        >
          Go to this project&apos;s runs
        </Link>
        <Link to="/runs" className="text-muted-foreground underline-offset-4 hover:underline">
          All runs
        </Link>
      </div>
    </Card>
  );
}
